import { InputField } from 'lib/iterable-hook-form';
import styled from 'styled-components';
import { FormField } from 'ui/formField';
import { InputText } from 'ui/inputs';
import {
  body2,
  caption,
  captionBold,
  colors,
  fontWeights,
  overline,
  spacing,
  subtitle2,
  textColors,
} from 'ui/theme';
import { Tooltip } from 'ui/tooltip';
import { pxToRem } from 'utils/convert';

const customSpacing = {
  p60: pxToRem(60),
  p110: pxToRem(110),
  p120: pxToRem(120),
  p125: pxToRem(125),
};

export const SectionHeader = styled.div`
  ${overline};
  margin-bottom: ${spacing.base};
  color: ${textColors.textSecondary};
`;

export const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

export const TotalAudienceWrapper = styled.div`
  display: flex;
`;

export const TotalAudienceLabel = styled.div`
  ${caption};
  color: ${colors.textSecondary};
`;

export const TotalAudienceValue = styled.div`
  ${captionBold};
  font-weight: ${fontWeights.semiBold};
  color: ${colors.textPrimary};
`;

export const StyledWinningCriteriaSectionHeader = styled(SectionHeader)<{
  isFirstSection?: boolean;
}>`
  ${({ isFirstSection }) => (isFirstSection ? '' : `margin-top: ${spacing.plusHalf}`)};
`;

export const StyledRadioPercentSign = styled.span`
  ${body2};
  color: ${textColors.textSecondary};
`;

export const StyledOptions = styled.div`
  ${body2};
  color: ${textColors.textPrimary};
  display: inline-flex;
  align-items: center;
  gap: ${spacing.half};
`;

export const StyledFields = styled.div`
  ${body2};
  font-weight: ${fontWeights.regular};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-right: ${spacing.single};
`;

export const StyledSecondaryOptionLabel = styled.div`
  ${body2};
  display: flex;
  gap: ${spacing.half};
  align-items: center;
  color: ${textColors.textSecondary} !important;
`;

export const StyledBaseGrid = styled.div`
  display: grid;
  grid-row-gap: ${spacing.half};
  grid-column-gap: ${spacing.threeQuarter};
  grid-template-rows: 1fr auto;
  grid-template-columns: 1fr 2fr;
`;

export const StyledHoldoutGrid = styled(StyledBaseGrid)`
  grid-template-areas: 'holdout-section-text holdoutText' '. holdoutSize' '. attributionTime';
`;

export const StyledHoldoutSectionText = styled.div`
  ${subtitle2};
  grid-area: holdout-section-text;
`;

export const StyledHoldoutTextGridSection = styled.div`
  ${body2};
  grid-area: holdoutText;
  display: flex;
  align-items: center;
`;

export type StyledHoldoutSizeGridSectionProps = {
  isShown?: boolean;
};

export const StyledHoldoutSizeGridSection = styled.div<StyledHoldoutSizeGridSectionProps>`
  grid-area: holdoutSize;
  padding-left: ${spacing.double};
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

export const StyledAttributionTimeGridSection = styled.div`
  grid-area: attributionTime;
  padding-left: ${spacing.double};
`;

export const StyledExperimentGroupSizeGrid = styled(StyledBaseGrid)<{
  showAttributionSize: boolean;
}>`
  grid-template-areas: ${({ showAttributionSize }) =>
    showAttributionSize
      ? `
  'test-strategy-section-text attributionCheckbox'
  '. attributionSize'`
      : `'test-strategy-section-text attributionCheckbox'`};
  grid-template-columns: 1fr 2fr;
  grid-template-rows: 1fr;
  grid-row-gap: 0;
`;

export const StyledTestStrategySectionText = styled.div`
  ${subtitle2};
  grid-area: test-strategy-section-text;
`;

export const StyledAttributionCheckboxGridSection = styled.div`
  grid-area: attributionCheckbox;
  display: flex;
`;

export const StyledAttributionSizeGridSection = styled.div`
  grid-area: attributionSize;
  padding-left: ${spacing.double};
`;

export const StyledCheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${spacing.plusHalf};
  height: ${spacing.plusHalf};
  margin-right: ${spacing.half};
`;

export const StyledRadioListWrapper = styled.div`
  display: flex;
  flex-direction: column;

  > :nth-child(2) {
    margin-bottom: 0;
  }
`;

export const StyledInputField = styled(InputField)`
  flex: 1;
  > input {
    width: 50%;
  }
`;

export const StyledInputText = styled(InputText)`
  flex: 1;
  > input {
    width: 50%;
  }
`;

export const StyledSmallerInputText = styled(InputField)`
  height: ${spacing.double};
  > input {
    width: ${customSpacing.p60};
  }
  width: ${customSpacing.p125};
`;

export const StyledTestDurationHoursInputs = styled(FormField)`
  display: flex;
  gap: ${spacing.base};
  color: ${textColors.textPrimary};
  width: 100%;
  ${subtitle2};
  align-items: center;

  > div {
    display: flex;
    gap: ${spacing.base};

    #test-duration-hours-error {
      display: none;
    }
  }
`;

export const StyledErrorMessage = styled.div`
  ${caption};
  color: ${colors.red100};
  padding-top: ${spacing.half};
`;

export const StyledWinningCriteriaWrapper = styled.div`
  ${subtitle2};
  display: flex;
  padding-right: ${spacing.eighth};
  gap: ${spacing.plusQuarter};
  > div:nth-child(1) {
    flex: 1;
  }
`;

export const StyledPaddedCriteriaWrapper = styled.div`
  ${subtitle2};
  display: flex;
  justify-content: start;
  padding-right: ${spacing.eighth};
  padding-top: ${spacing.base};
  gap: ${spacing.plusQuarter};
`;

export const StyledSendTimeWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.base};
  flex: 2;
`;

export const StyledTimeZonePickerWrapper = styled.div`
  flex: 2;

  #time-zone-picker {
    width: ${customSpacing.p120};
  }
`;

export const StyledUsersTimezoneLabel = styled.div`
  flex: 1;
`;

export const StyledLabelWrapper = styled.div`
  ${body2};
`;

export const StyledWinningCriteriaDropdown = styled.div`
  flex: 2;
`;

export const StyledInputWrapper = styled.div`
  flex: 2;
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
`;

export const StyledDurationLabel = styled.div`
  flex: 1;
`;

export const SettingsSectionDivider = styled.div`
  height: ${spacing.plusHalf};
`;

export const StyledInfoTooltip = styled(Tooltip)`
  display: flex;
  align-items: center;
`;

export const AddHoldoutGroupLabel = styled.div<{ isDisabled: boolean }>`
  ${({ isDisabled }) => (isDisabled ? `color: ${colors.textDisabled}` : ``)};
`;
